import { COMMON_TEXT } from "@/constants/text";
import { insepectTypeStatus } from "@/utils/inspectTypeStatus";
import timeUtil from "@/utils/timeUtil";
import { Checkbox, Select, Table, TextInput } from "@mantine/core";
import {
  SpcApiInspectionsInspectionIdSubmitPostRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useEffect } from "react";

interface Props {
  criteriaList:
    | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner[]
    | undefined;
  recordForm: {
    values: SpcApiInspectionsInspectionIdSubmitPostRequest;
    setFieldValue: (path: string, value: any) => void;
  };
  detail?: boolean;
  acceptanceStatus?: boolean;
  // 사용자 입력으로 값이 변경되었을 때 호출 (부모에서 dirty 처리 등)
  onAnyChange?: (nextRecord: any[][]) => void;
}

export const InspectTables = ({
  criteriaList,
  recordForm,
  detail = false,
  acceptanceStatus = false,
  onAnyChange,
}: Props) => {
  const computeIsPassed = (
    criteria:
      | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner
      | undefined,
    rawValue: any
  ): boolean | undefined | null => {
    if (!criteria) return undefined;
    switch (criteria.type) {
      case "boolean": {
        if (
          typeof (criteria as any)?.standard === "boolean" &&
          typeof rawValue === "boolean"
        ) {
          return rawValue === (criteria as any).standard;
        }
        return undefined;
      }
      case "number": {
        const min =
          (criteria as any)?.min != null
            ? Number((criteria as any).min)
            : undefined;
        const max =
          (criteria as any)?.max != null
            ? Number((criteria as any).max)
            : undefined;
        const v =
          rawValue != null && rawValue !== "" ? Number(rawValue) : undefined;
        if (v == null || Number.isNaN(v)) return undefined;
        if (min != null && max != null) return v >= min && v <= max;
        if (min != null) return v >= min;
        if (max != null) return v <= max;
        return undefined;
      }
      case "select": {
        const standards: any[] = Array.isArray((criteria as any)?.standard)
          ? ((criteria as any).standard as any[])
          : [];
        return standards.length ? standards.includes(rawValue) : undefined;
      }
      case "description": {
        // description은 value와 무관하게 체크박스로만 합격 처리
        return undefined;
      }
      default:
        return undefined;
    }
  };
  const TYPE_HEADERS: Record<string, string[]> = {
    number: [
      COMMON_TEXT.INSPECT.POINTNAME,
      COMMON_TEXT.INSPECT.TYPE,
      COMMON_TEXT.INSPECT.SAMPLENO,
      COMMON_TEXT.INSPECT.VALUE,
      COMMON_TEXT.INSPECT.PASSRANGE,
      ...(acceptanceStatus
        ? [
            COMMON_TEXT.INSPECT.ACCEPTANCESTATUS,
            COMMON_TEXT.INSPECT.UPDATEDAT,
            COMMON_TEXT.INSPECT.UPDATERUSERCODE,
          ]
        : []),
    ],
    select: [
      COMMON_TEXT.INSPECT.POINTNAME,
      COMMON_TEXT.INSPECT.TYPE,
      COMMON_TEXT.INSPECT.SAMPLENO,
      COMMON_TEXT.INSPECT.VALUE,
      COMMON_TEXT.INSPECT.PASSSTANDARD,
      ...(acceptanceStatus
        ? [
            COMMON_TEXT.INSPECT.ACCEPTANCESTATUS,
            COMMON_TEXT.INSPECT.UPDATEDAT,
            COMMON_TEXT.INSPECT.UPDATERUSERCODE,
          ]
        : []),
    ],
    boolean: [
      COMMON_TEXT.INSPECT.POINTNAME,
      COMMON_TEXT.INSPECT.TYPE,
      COMMON_TEXT.INSPECT.SAMPLENO,
      COMMON_TEXT.INSPECT.VALUE,
      COMMON_TEXT.INSPECT.PASSSTANDARD,
      ...(acceptanceStatus
        ? [
            COMMON_TEXT.INSPECT.ACCEPTANCESTATUS,
            COMMON_TEXT.INSPECT.UPDATEDAT,
            COMMON_TEXT.INSPECT.UPDATERUSERCODE,
          ]
        : []),
    ],
    description: [
      COMMON_TEXT.INSPECT.POINTNAME,
      COMMON_TEXT.INSPECT.TYPE,
      COMMON_TEXT.INSPECT.SAMPLENO,
      COMMON_TEXT.INSPECT.DESCRIPTION,
      COMMON_TEXT.INSPECT.CHECKPASS,
      ...(acceptanceStatus
        ? [
            COMMON_TEXT.INSPECT.ACCEPTANCESTATUS,
            COMMON_TEXT.INSPECT.UPDATEDAT,
            COMMON_TEXT.INSPECT.UPDATERUSERCODE,
          ]
        : []),
    ],
  };

  // 초기 셀 보정: 기존 데이터가 primitive인 경우 보존하여 { value } 형태로 변환
  useEffect(() => {
    if (!criteriaList || criteriaList.length === 0) return;
    const record =
      recordForm.values.inspectionsInspectionIdSubmitPostRequest.record;
    if (!record) return;

    const getDefaultValue = (type?: string) => {
      switch (type) {
        case "boolean":
          return false;
        case "number":
          return null;
        case "select":
          return null;
        case "description":
        default:
          return null;
      }
    };

    const normalizePrimitive = (raw: any, type?: string) => {
      if (raw == null) return getDefaultValue(type);
      switch (type) {
        case "boolean":
          return Boolean(raw);
        case "number":
          return raw === "" || raw == null ? null : Number(raw);
        case "select":
        case "description":
        default:
          return raw;
      }
    };

    criteriaList.forEach((criteria, ci) => {
      const sampleNum = criteria.sampleNum ?? 1;
      if (!record[ci]) {
        recordForm.setFieldValue(
          `inspectionsInspectionIdSubmitPostRequest.record.${ci}`,
          Array.from({ length: sampleNum }).map(() => ({
            value: getDefaultValue(criteria.type),
          }))
        );
        return;
      }
      for (let ri = 0; ri < sampleNum; ri++) {
        const cell = record?.[ci]?.[ri] as unknown;
        // 이미 올바른 객체 형태면 skip
        if (
          cell &&
          typeof cell === "object" &&
          (cell as any).hasOwnProperty("value")
        )
          continue;
        // primitive 또는 null/undefined면 보존 변환하여 설정
        const normalized = normalizePrimitive(cell, criteria.type);
        recordForm.setFieldValue(
          `inspectionsInspectionIdSubmitPostRequest.record.${ci}.${ri}`,
          { value: normalized }
        );
      }
    });
  }, [criteriaList]);

  if (!criteriaList || criteriaList.length === 0) return null;

  const renderRecordCell = (
    criteriaIdx: number,
    rowIdx: number,
    type: "acceptance" | "determineAt" | "updater"
  ) => {
    const cell = recordForm.values.inspectionsInspectionIdSubmitPostRequest
      .record?.[criteriaIdx]?.[rowIdx] as
      | { isPassed?: boolean; determineAt?: string; updaterUserName?: string }
      | undefined;

    if (!cell) return "-";

    switch (type) {
      case "acceptance":
        if (cell.isPassed === true) return COMMON_TEXT.INSPECT.PASS;
        if (cell.isPassed === false) return COMMON_TEXT.INSPECT.FAIL;
        return "-";
      case "determineAt":
        return timeUtil(cell.determineAt) ?? "-";
      case "updater":
        return cell.updaterUserName ?? "-";
      default:
        return "-";
    }
  };

  return (
    <>
      {["boolean", "number", "select", "description"].map((type) => {
        if (!criteriaList?.some((c) => c.type === (type as any))) return null;
        return (
          <Table
            withColumnBorders
            withBorder
            striped
            highlightOnHover
            mb={24}
            key={type}
          >
            <thead>
              <tr>
                {TYPE_HEADERS[type].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteriaList.map((criteria, criteriaIdx) => {
                if (criteria.type !== (type as any)) return null;
                const sampleNum = criteria.sampleNum ?? 1;
                return Array.from({ length: sampleNum }).map((_, rowIdx) => (
                  <tr key={`${type}-${criteriaIdx}-${rowIdx}`}>
                    {rowIdx === 0 && (
                      <>
                        <td rowSpan={sampleNum}>{criteria.name}</td>
                        <td rowSpan={sampleNum}>
                          {insepectTypeStatus[criteria.type]}
                        </td>
                      </>
                    )}
                    <td>{rowIdx + 1}</td>

                    {type === "number" && (
                      <>
                        <td>
                          <TextInput
                            type="number"
                            disabled={detail}
                            value={
                              (recordForm.values
                                .inspectionsInspectionIdSubmitPostRequest
                                .record?.[criteriaIdx]?.[rowIdx]?.value as
                                | number
                                | null) ?? ""
                            }
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              const parsed = v === "" ? null : Number(v);
                              const base =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record ?? [];
                              const rowArr = base[criteriaIdx] ?? [];
                              const prevCell = rowArr[rowIdx] ?? {
                                value: null,
                              };
                              const nextCell = {
                                ...prevCell,
                                value: parsed,
                                isPassed:
                                  computeIsPassed(criteria, parsed) ??
                                  prevCell?.isPassed,
                              };
                              recordForm.setFieldValue(
                                `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.${rowIdx}`,
                                nextCell
                              );
                              const cur =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record;
                              const nextRecord = cur.map((row) =>
                                row.map((cell) => ({ ...cell }))
                              );
                              nextRecord[criteriaIdx][rowIdx] = nextCell as any;
                              onAnyChange?.(nextRecord as any);
                            }}
                          />
                        </td>
                        <td>
                          {criteria.min} ~ {criteria.max}
                        </td>
                        {acceptanceStatus && (
                          <>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "acceptance"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "determineAt"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(criteriaIdx, rowIdx, "updater")}
                            </td>
                          </>
                        )}
                      </>
                    )}

                    {type === "select" && (
                      <>
                        <td>
                          <Select
                            data={
                              criteria.options?.map((opt) => ({
                                value: opt as string,
                                label: opt as string,
                              })) ?? []
                            }
                            disabled={detail}
                            value={
                              (recordForm.values
                                .inspectionsInspectionIdSubmitPostRequest
                                .record?.[criteriaIdx]?.[rowIdx]?.value as
                                | string
                                | null) ?? null
                            }
                            onChange={(val) => {
                              const base =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record ?? [];
                              const rowArr = base[criteriaIdx] ?? [];
                              const prevCell = rowArr[rowIdx] ?? {
                                value: null,
                              };
                              const nextCell = {
                                ...prevCell,
                                value: val,
                                isPassed:
                                  computeIsPassed(criteria, val) ??
                                  prevCell?.isPassed,
                              };
                              recordForm.setFieldValue(
                                `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.${rowIdx}`,
                                nextCell
                              );
                              const cur =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record;
                              const nextRecord = cur.map((row) =>
                                row.map((cell) => ({ ...cell }))
                              );
                              nextRecord[criteriaIdx][rowIdx] = nextCell as any;
                              onAnyChange?.(nextRecord as any);
                            }}
                          />
                        </td>
                        <td>
                          {Array.isArray(criteria.standard)
                            ? criteria.standard
                            : [].join(", ")}
                        </td>
                        {acceptanceStatus && (
                          <>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "acceptance"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "determineAt"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(criteriaIdx, rowIdx, "updater")}
                            </td>
                          </>
                        )}
                      </>
                    )}

                    {type === "boolean" && (
                      <>
                        <td>
                          <Checkbox
                            disabled={detail}
                            checked={
                              !!recordForm.values
                                .inspectionsInspectionIdSubmitPostRequest
                                .record?.[criteriaIdx]?.[rowIdx]?.value
                            }
                            onChange={(e) => {
                              const base =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record ?? [];
                              const rowArr = base[criteriaIdx] ?? [];
                              const prevCell = rowArr[rowIdx] ?? {
                                value: false,
                              };
                              const nextCell = {
                                ...prevCell,
                                value: e.currentTarget.checked,
                                isPassed:
                                  computeIsPassed(
                                    criteria,
                                    e.currentTarget.checked
                                  ) ?? prevCell?.isPassed,
                              };
                              recordForm.setFieldValue(
                                `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.${rowIdx}`,
                                nextCell
                              );
                              const cur =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record;
                              const nextRecord = cur.map((row) =>
                                row.map((cell) => ({ ...cell }))
                              );
                              nextRecord[criteriaIdx][rowIdx] = nextCell as any;
                              onAnyChange?.(nextRecord as any);
                            }}
                          />
                        </td>
                        <td>
                          {criteria.standard
                            ? COMMON_TEXT.INSPECT.CHECKPASS
                            : COMMON_TEXT.INSPECT.FAIL}
                        </td>
                        {acceptanceStatus && (
                          <>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "acceptance"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "determineAt"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(criteriaIdx, rowIdx, "updater")}
                            </td>
                          </>
                        )}
                      </>
                    )}

                    {type === "description" && (
                      <>
                        <td>
                          <TextInput
                            disabled={detail}
                            value={
                              (recordForm.values
                                .inspectionsInspectionIdSubmitPostRequest
                                .record?.[criteriaIdx]?.[rowIdx]?.value as
                                | string
                                | null) ?? ""
                            }
                            onChange={(e) => {
                              const val = e.currentTarget.value;
                              const base =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record ?? [];
                              const rowArr = base[criteriaIdx] ?? [];
                              const prevCell = rowArr[rowIdx] ?? {
                                value: "",
                                isPassed: null,
                              };
                              const nextCell = {
                                ...prevCell,
                                value: val,
                                // If user manually set pass/fail via checkbox, preserve it; otherwise compute
                                isPassed:
                                  prevCell?.isPassed === true ||
                                  prevCell?.isPassed === false
                                    ? prevCell.isPassed
                                    : computeIsPassed(criteria, val),
                              };
                              recordForm.setFieldValue(
                                `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.${rowIdx}`,
                                nextCell
                              );
                              const cur =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record;
                              const nextRecord = cur.map((row) =>
                                row.map((cell) => ({ ...cell }))
                              );
                              nextRecord[criteriaIdx][rowIdx] = nextCell as any;
                              onAnyChange?.(nextRecord as any);
                            }}
                          />
                        </td>
                        <td>
                          <Checkbox
                            disabled={detail}
                            checked={
                              !!recordForm.values
                                .inspectionsInspectionIdSubmitPostRequest
                                .record?.[criteriaIdx]?.[rowIdx]?.isPassed
                            }
                            onChange={(e) => {
                              const base =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record ?? [];
                              const rowArr = base[criteriaIdx] ?? [];
                              const prevCell = rowArr[rowIdx] ?? {
                                value: "",
                              };
                              const nextCell = {
                                ...prevCell,
                                value: prevCell.value,
                                isPassed: e.currentTarget.checked
                                  ? true
                                  : false,
                              };
                              recordForm.setFieldValue(
                                `inspectionsInspectionIdSubmitPostRequest.record.${criteriaIdx}.${rowIdx}`,
                                nextCell
                              );
                              const cur =
                                recordForm.values
                                  .inspectionsInspectionIdSubmitPostRequest
                                  .record;
                              const nextRecord = cur.map((row) =>
                                row.map((cell) => ({ ...cell }))
                              );
                              nextRecord[criteriaIdx][rowIdx] = nextCell as any;
                              onAnyChange?.(nextRecord as any);
                            }}
                          />
                        </td>
                        {acceptanceStatus && (
                          <>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "acceptance"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(
                                criteriaIdx,
                                rowIdx,
                                "determineAt"
                              )}
                            </td>
                            <td>
                              {renderRecordCell(criteriaIdx, rowIdx, "updater")}
                            </td>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                ));
              })}
            </tbody>
          </Table>
        );
      })}
    </>
  );
};

export default InspectTables;
